import type { PanelToastAction } from "@portal/components/patterns/panel-toast";
import { useGlobalErrorMessageModal } from "@portal/providers/GlobalErrorHandlingProvider";
import { createContext, type ReactNode, useCallback, useContext } from "react";
import { isAuthenticationError, logError } from "../../utils/error";

/** A follow-up offered with a failure, e.g. reopening the form whose save failed. */
export type MutationFailureAction = PanelToastAction;

export type MutationFailure = {
	error: Error;
	/** What failed, in the underwriter's terms: "Couldn't request Bank statements." */
	message: string;
	action?: MutationFailureAction;
};

type ReportMutationFailure = (failure: MutationFailure) => void;

const MutationFailureContext = createContext<ReportMutationFailure | null>(null);

/**
 * Where decisioning write failures surface. The panel provides one that shows its own toast;
 * without a provider they fall back to the global error modal.
 */
export const MutationFailureProvider = ({
	onFailure,
	children,
}: {
	onFailure: ReportMutationFailure;
	children: ReactNode;
}) => (
	<MutationFailureContext.Provider value={onFailure}>{children}</MutationFailureContext.Provider>
);

/** Logs the failure, then reports it to the nearest `MutationFailureProvider`. */
export const useReportMutationFailure = (): ReportMutationFailure => {
	const report = useContext(MutationFailureContext);
	const { triggerErrorModal } = useGlobalErrorMessageModal();

	return useCallback(
		(failure) => {
			if (isAuthenticationError(failure.error)) return;
			logError(failure.error);
			if (report) report(failure);
			else triggerErrorModal(failure.error, { description: failure.message });
		},
		[report, triggerErrorModal],
	);
};
